import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, Sparkles } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const PricingPage = () => {
  // FLAT-PRICE BATHROOM PACKAGES (including new Powder Room)
  const bathroomPricing = [
    {
      name: 'Powder Room',
      price: '$6,500',
      description: 'Our lowest entry point — ideal for a first project.',
      scopeQualifier: 'Compact 2-piece bathroom.',
      features: [
        'Compact 2-piece layout',
        'New vanity & fixtures',
        'Tile flooring',
        'Fresh paint & trim',
      ],
      popular: false,
      isNew: true,
    },
    {
      name: 'Essential Bathroom',
      price: '$7,900',
      description: 'Quality bathroom refresh with standard fixtures, new tile, and clean finishes. Perfect for functional upgrades.',
      scopeQualifier: 'Standard 3-piece, up to 50 sq ft.',
      features: [
        'Standard fixtures & hardware',
        'Ceramic tile flooring',
        'New vanity & countertop',
        'Updated lighting',
        'Fresh paint & trim',
      ],
      popular: false,
    },
    {
      name: 'Signature Bathroom',
      price: '$12,900',
      description: 'Elevated bathroom design with upgraded fixtures, premium tile, and enhanced storage solutions.',
      scopeQualifier: 'Full 3-piece or 4-piece, up to 75 sq ft.',
      features: [
        'Upgraded fixtures & faucets',
        'Porcelain or natural stone tile',
        'Custom vanity options',
        'Frameless glass shower',
        'Improved ventilation',
        'Heated flooring option',
      ],
      popular: true,
    },
    {
      name: 'Custom Bathroom',
      price: '$25,000+',
      description: 'Luxury spa-inspired bathroom with premium finishes, custom features, and high-end materials throughout.',
      scopeQualifier: 'Large or master bath with custom layout.',
      features: [
        'Designer fixtures & hardware',
        'Premium marble or terrazzo',
        'Custom cabinetry & storage',
        'Walk-in rainfall shower',
        'Freestanding soaker tub',
        'Custom lighting design',
        'Heated floors & towel bars',
      ],
      popular: false,
    },
  ];

  // FLAT-PRICE BASEMENT STARTING POINT
  const basementFlatPrice = {
    name: 'Essential Basement',
    flatPrice: '$35,000',
    ratePrice: '$45',
    unit: 'per sq ft',
    description: 'Clean, functional basement space with high-quality standard materials. Ideal for practical and durable finishing.',
    scopeQualifier: 'Entry rate assumes up to 850 sq ft.',
    exampleLine: 'Typical 1,000 sq ft basement: from $42,000.',
    features: [
      'Standard drywall & paint',
      'Basic lighting package',
      'Standard flooring options',
      'Code-compliant electrical',
      'Basic trim & finishing',
    ],
  };

  // PER SQ FT BASEMENT PRICING
  const basementPerSqFt = [
    {
      name: 'Signature Finish',
      price: '$62',
      unit: 'per sq ft',
      description: 'Balanced design with upgraded finishes, improved lighting, and enhanced details. Our most popular option in Calgary.',
      scopeQualifier: 'Recommended for 600–1,200 sq ft.',
      exampleLine: 'Typical 1,000 sq ft basement: from $62,000.',
      features: [
        'Premium drywall finishing',
        'Upgraded lighting design',
        'Enhanced flooring selection',
        'Improved electrical layout',
        'Quality trim & moldings',
        'Feature accent options',
      ],
      popular: true,
    },
    {
      name: 'Custom Finish',
      price: '$85–95',
      unit: 'per sq ft',
      description: 'Fully tailored layout, premium materials, custom features, built-ins, feature walls, and modern luxury elements.',
      scopeQualifier: 'Ideal for custom layouts and high-end finishes.',
      exampleLine: 'Typical 1,000 sq ft basement: from $85,000.',
      features: [
        'Custom design & layout',
        'Premium materials throughout',
        'Custom built-ins & storage',
        'Feature walls & fireplaces',
        'High-end lighting design',
        'Luxury flooring options',
        'Architectural details',
      ],
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            Transparent Pricing
          </p>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
            Investment & Project Pricing
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Quality renovations at fair prices. We provide detailed estimates with no hidden costs, 
            so you know exactly what to expect before we begin.
          </p>
        </div>
      </section>

      {/* SECTION 1 - Bathroom Renovations (Flat Pricing) */}
      <section className="py-20 md:py-28 bg-white" data-testid="bathroom-pricing-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              Fixed-Price Packages
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-4">
              Bathroom Renovations
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              From functional updates to luxury spa-inspired retreats. 
              We create bathrooms that elevate your daily routine.
            </p>
          </div>

          {/* Pricing Cards - 4 columns on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bathroomPricing.map((tier, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                  tier.popular 
                    ? 'ring-2 ring-[#c5a059] shadow-xl' 
                    : 'border border-slate-200 shadow-lg hover:shadow-xl'
                }`}
                data-testid={`bathroom-tier-${index}`}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-[#c5a059] text-[#0f172a] text-xs font-semibold uppercase tracking-wider text-center py-2">
                    Most Popular
                  </div>
                )}
                {tier.isNew && (
                  <div className="absolute top-0 left-0 right-0 bg-[#0f172a] text-[#c5a059] text-xs font-semibold uppercase tracking-wider text-center py-2 flex items-center justify-center gap-1">
                    <Sparkles size={12} />
                    Entry Point
                  </div>
                )}
                <div className={`p-6 ${tier.popular || tier.isNew ? 'pt-12' : ''}`}>
                  <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-3">
                    {tier.name}
                  </h3>
                  <div className="mb-1">
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Starting from</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-heading text-3xl font-bold text-[#c5a059]">
                      {tier.price}
                    </span>
                  </div>
                  {/* Scope Qualifier */}
                  <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                    {tier.scopeQualifier}
                  </p>
                  <p className="text-slate-600 text-sm mb-5 min-h-[48px]">
                    {tier.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <Check size={14} className="text-[#c5a059] flex-shrink-0 mt-0.5" />
                        <span className="text-xs">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`block w-full text-center py-3 font-semibold text-xs uppercase tracking-wider transition-colors ${
                      tier.popular
                        ? 'bg-[#0f172a] text-white hover:bg-[#1e293b]'
                        : 'bg-[#f8f9fa] text-[#0f172a] hover:bg-[#0f172a] hover:text-white'
                    }`}
                  >
                    Request Free Estimate
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Scope Qualifier Note */}
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400">
              Not included: structural changes, plumbing relocation, permit fees.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-10 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#0f172a] text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-[#1e293b] transition-colors"
              data-testid="bathroom-section-cta"
            >
              Get Your Free Bathroom Estimate
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 2 - Basement Development (Flat Entry + Per Sq Ft) */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]" data-testid="basement-pricing-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              Basement Development
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-4">
              Transform Your Basement
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Transform your unfinished basement into beautiful living space. 
              Choose the finish level that fits your vision and budget.
            </p>
          </div>

          {/* Flat-Price Entry Card */}
          <div className="mb-12">
            <p className="text-center text-sm text-slate-500 uppercase tracking-wider mb-6">
              Fixed Starting Price
            </p>
            <div className="max-w-xl mx-auto">
              <div className="bg-white border-2 border-[#c5a059] rounded-sm shadow-xl p-8">
                <h3 className="font-heading text-2xl font-semibold text-[#0f172a] mb-4 text-center">
                  {basementFlatPrice.name}
                </h3>
                <div className="text-center mb-2">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">From</span>
                </div>
                <div className="text-center mb-2">
                  <span className="font-heading text-5xl font-bold text-[#c5a059]">
                    {basementFlatPrice.flatPrice}
                  </span>
                </div>
                <div className="text-center mb-4">
                  <span className="text-sm text-slate-500">
                    or {basementFlatPrice.ratePrice} {basementFlatPrice.unit}
                  </span>
                </div>
                {/* Scope Qualifier */}
                <p className="text-xs text-slate-400 text-center mb-4">
                  {basementFlatPrice.scopeQualifier}
                </p>
                <p className="text-slate-600 text-center mb-6">
                  {basementFlatPrice.description}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {basementFlatPrice.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600">
                      <Check size={16} className="text-[#c5a059] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                {/* Example Line */}
                <div className="bg-[#f8f9fa] p-3 rounded text-center mb-6">
                  <p className="text-sm text-[#0f172a] font-medium">
                    {basementFlatPrice.exampleLine}
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="block w-full text-center py-4 bg-[#0f172a] text-white font-semibold text-sm uppercase tracking-wider hover:bg-[#1e293b] transition-colors"
                >
                  Request Free Estimate
                </Link>
              </div>
            </div>
          </div>

          {/* Per Sq Ft Rate Cards */}
          <div>
            <p className="text-center text-sm text-slate-500 uppercase tracking-wider mb-6">
              Premium Finish Rates
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {basementPerSqFt.map((tier, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                    tier.popular 
                      ? 'ring-2 ring-[#c5a059] shadow-xl' 
                      : 'border border-slate-200 shadow-lg hover:shadow-xl'
                  }`}
                  data-testid={`basement-tier-${index}`}
                >
                  {tier.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-[#c5a059] text-[#0f172a] text-xs font-semibold uppercase tracking-wider text-center py-2">
                      Most Popular
                    </div>
                  )}
                  <div className={`p-8 ${tier.popular ? 'pt-14' : ''}`}>
                    <h3 className="font-heading text-2xl font-semibold text-[#0f172a] mb-2">
                      {tier.name}
                    </h3>
                    <div className="mb-1">
                      <span className="text-xs text-slate-500 uppercase tracking-wider">Starting from</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="font-heading text-4xl font-bold text-[#c5a059]">
                        {tier.price}
                      </span>
                      <span className="text-slate-500">{tier.unit}</span>
                    </div>
                    {/* Scope Qualifier */}
                    <p className="text-xs text-slate-400 mb-4">
                      {tier.scopeQualifier}
                    </p>
                    <p className="text-slate-600 mb-6 min-h-[60px]">
                      {tier.description}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-600">
                          <Check size={18} className="text-[#c5a059] flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {/* Example Line */}
                    <div className="bg-[#f8f9fa] p-3 rounded mb-6">
                      <p className="text-sm text-[#0f172a] font-medium text-center">
                        {tier.exampleLine}
                      </p>
                    </div>
                    <Link
                      to="/contact"
                      className={`block w-full text-center py-4 font-semibold text-sm uppercase tracking-wider transition-colors ${
                        tier.popular
                          ? 'bg-[#0f172a] text-white hover:bg-[#1e293b]'
                          : 'bg-[#f8f9fa] text-[#0f172a] hover:bg-[#0f172a] hover:text-white'
                      }`}
                    >
                      Request Free Estimate
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trust Line */}
          <div className="mt-12 bg-[#0f172a] p-6 rounded-sm max-w-3xl mx-auto text-center">
            <p className="text-white text-sm">
              <span className="text-[#c5a059] font-semibold">Did you know?</span> City of Calgary reimburses up to $10,000 on legal secondary suites.
            </p>
          </div>

          {/* Scope Note */}
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400">
              Not included: structural changes, plumbing relocation, permit fees.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-10 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#0f172a] text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-[#1e293b] transition-colors"
              data-testid="basement-section-cta"
            >
              Get Your Free Basement Estimate
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Full Interior Renovations */}
      <section className="py-20 md:py-28 bg-white" data-testid="interior-pricing-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              Comprehensive Renovations
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-4">
              Full Interior Renovations
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Complete home transformations tailored to your lifestyle.
            </p>
          </div>

          {/* Single Large Card */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#0f172a] text-white p-8 md:p-12 rounded-sm shadow-xl">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-6">
                <div>
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-2">
                    Full Home Renovations
                  </h3>
                  <p className="text-slate-300">
                    Comprehensive interior renovation services
                  </p>
                </div>
                <div className="text-left md:text-right">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Starting from</p>
                  <p className="font-heading text-4xl sm:text-5xl font-bold text-[#c5a059]">
                    $85
                  </p>
                  <p className="text-sm text-slate-400">per sq ft</p>
                </div>
              </div>
              
              {/* Scope Qualifier */}
              <div className="bg-white/5 border border-white/10 p-4 rounded mb-6">
                <p className="text-sm text-slate-300 leading-relaxed">
                  $85/sq ft covers cosmetic-to-moderate scope. Structural work, layout changes and load-bearing modifications are quoted separately.
                </p>
              </div>

              <p className="text-slate-300 mb-6 leading-relaxed">
                Full interior renovations depending on scope and complexity. Our team handles everything 
                from design planning to final finishing, ensuring a seamless transformation of your entire 
                living space. Includes structural modifications, electrical and plumbing upgrades, 
                custom millwork, and premium finishes throughout.
              </p>

              {/* Example Line */}
              <div className="bg-[#c5a059]/10 border border-[#c5a059]/30 p-4 rounded mb-6">
                <p className="text-[#c5a059] font-semibold text-center">
                  Typical 1,500 sq ft home: from $127,500
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {['Kitchen Remodels', 'Open Concept Layouts', 'Custom Millwork', 'Premium Finishes'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check size={16} className="text-[#c5a059] flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              {/* Scope Note */}
              <p className="text-xs text-slate-400 mb-6">
                Not included: structural changes, plumbing relocation, permit fees.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
                data-testid="interior-section-cta"
              >
                Discuss Your Project
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Contact us for a free, no-obligation estimate. We'll visit your home, discuss your vision, 
            and provide detailed pricing within 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-[#0f172a] text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-[#1e293b] transition-colors"
              data-testid="pricing-final-cta"
            >
              Request Free Estimate
              <ArrowRight size={18} />
            </Link>
            <a
              href="tel:403-402-6596"
              className="inline-flex items-center justify-center gap-3 border-2 border-[#0f172a] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-[#0f172a] hover:text-white transition-colors"
            >
              <Phone size={18} />
              Call 403-402-6596
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
